import { DATA_CATALOG } from '@elementary/common-ui/constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AUTH_TYPES, DataCatalogForm } from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import {
  clearOptionalSecret,
  clearSecretIndication,
} from 'shared/utils/secretUtils';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useFlags from '../useFlags';
import useSendTracking from './useSendTracking';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useDataCatalogIntegration = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { hasFlag } = useFlags();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const dataCatalogConfigQuery = useQuery(
    [DATA_CATALOG, envId],
    () => api.fetchDataCatalogs(envId!),
    {
      enabled: !!envId && hasFlag('metastore'),
      select: (data) => data.integrations || [],
    },
  );

  const dataCatalogMutation = useMutation(
    (data: DataCatalogForm) => {
      if (data.selectedMetaStore === 'GLUE') {
        const reqData = {
          name: data.glueConnectionName.trim(),
          config: {
            catalog_type: data.selectedMetaStore,
            region: data.glueRegion,
            auth: {
              ...(data.glueAuthMethod === AUTH_TYPES.role
                ? {
                    auth_type: AUTH_TYPES.role as const,
                    role_arn: data.glueRoleArn,
                    external_id: clearOptionalSecret(data.glueExternalId),
                  }
                : {
                    auth_type: AUTH_TYPES.access_key as const,
                    access_key_id: clearSecretIndication(data.glueAccessKeyId)!,
                    secret_access_key: clearSecretIndication(
                      data.glueSecretAccessKey,
                    )!,
                    session_token: clearOptionalSecret(data.glueSessionToken),
                  }),
            },
          },
        };

        return data.id
          ? api.updateDataCatalog({ data: reqData, id: data.id, envId: envId! })
          : api.setDataCatalog({ data: reqData, envId: envId! });
      }

      if (data.selectedMetaStore === 'BIGLAKE') {
        const reqData = {
          name: data.bigLakeConnectionName.trim(),
          config: {
            catalog_type: data.selectedMetaStore,
            catalog_name: data.bigLakeCatalogName,
            project: data.bigLakeProject || undefined,
            catalog_location: data.bigLakeCatalogLocation || undefined,
          },
        };

        return data.id
          ? api.updateDataCatalog({ data: reqData, id: data.id, envId: envId! })
          : api.setDataCatalog({ data: reqData, envId: envId! });
      }

      return Promise.resolve();
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([DATA_CATALOG, envId]);
        openSnackbar({
          message: 'Successfully connected to your data catalog',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_DATA_CATALOG_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_DATA_CATALOG_FAILURE,
        }),
    },
  );

  const deleteCatalogMutation = useMutation(
    ({ id }: { id: string }) => api.deleteDataCatalog({ id, envId: envId! }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([DATA_CATALOG, envId]);
        openSnackbar({
          message: 'Data catalog connection removed',
          variant: 'success',
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_DATA_CATALOG_FAILURE,
        }),
    },
  );

  return {
    dataCatalogConfigQuery,
    dataCatalogMutation,
    deleteCatalogMutation,
  };
};

export default useDataCatalogIntegration;
